/**
 * Copyright © 2025 Delicious AI, LLC
 *
 * @author Stockton Jenkins <stockton.jenkins@deliciousai.com>
 */

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  Divider,
  IconButton,
  Pagination,
} from '@mui/material'
import DialogTitle from '@mui/material/DialogTitle'
import { CheckCircleIcon, XIcon } from 'lucide-react'
import React from 'react'

import { InsightMetadata } from '@/components/insights/types'
import useT from '@/i18n/useT'

type InsightDialogProps = {
  title: string
  open: boolean
  onClose: () => void
  onAcknowledge: (index: number) => void
  acknowledgedIds: Array<string>
  metadata: Array<InsightMetadata>
  renderFrame: (metadata: InsightMetadata) => React.ReactNode
}

const InsightDialog: React.FC<
  React.PropsWithChildren<InsightDialogProps>
> = props => {
  const {
    title,
    open,
    onAcknowledge,
    acknowledgedIds,
    onClose,
    metadata,
    renderFrame,
  } = props
  const { t } = useT()
  const [currentIndex, setCurrentIndex] = React.useState(0)
  const isAcknowledged = acknowledgedIds.includes(
    metadata[currentIndex].insightId,
  )
  const handleAcknowledge = () => {
    onAcknowledge(currentIndex)
    if (metadata.length > 1 && currentIndex < metadata.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      onClose()
    }
  }
  return (
    <Dialog onClose={onClose} open={open} fullWidth>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={theme => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <XIcon />
      </IconButton>
      <DialogTitle>{title}</DialogTitle>
      <Divider sx={{ mb: 4 }} />
      <Box sx={{ overflowX: 'hidden' }}>
        <Box
          sx={{
            width: `${100 * metadata.length}%`,
            display: 'flex',
            transition: 'transform 0.4s ease',
            transform: `translateX(-${(100 / metadata.length) * currentIndex}%)`,
          }}
        >
          {metadata.map((meta, index) => (
            <Box
              key={`insight-${index}`}
              sx={{
                width: `${100 / metadata.length}%`,
                px: 6,
                flexShrink: 0,
              }}
            >
              {renderFrame(meta)}
            </Box>
          ))}
        </Box>
      </Box>
      <DialogActions
        sx={{
          justifyContent: metadata.length > 1 ? 'space-between' : undefined,
        }}
      >
        <Button
          color={'info'}
          onClick={handleAcknowledge}
          disabled={isAcknowledged}
          startIcon={isAcknowledged ? <CheckCircleIcon /> : null}
        >
          {isAcknowledged
            ? t('insightDialog.acknowledged')
            : t('insightDialog.acknowledge')}
        </Button>
        {metadata.length > 1 && (
          <Pagination
            count={metadata.length}
            shape={'rounded'}
            page={1 + currentIndex}
            onChange={(_, value) => setCurrentIndex(value - 1)}
          />
        )}
      </DialogActions>
    </Dialog>
  )
}

export default InsightDialog
